import {
  EmptyState,
  ErrorState,
  LoadingState,
  Markdown,
  RADIUS,
  READING_MEASURE,
  SHADOW,
  Turnstile,
  WithHalfdot,
  Wordmark,
} from "@elyxeer/ui";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Link as RouterLink,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { EventQuestions } from "~/components/events/EventQuestions";
import { EventStateBadge } from "~/components/events/EventStateBadge";
import { EventTime } from "~/components/events/EventTime";
import { ArticleCover } from "~/components/news/ArticleCover";
import { ContentDisclaimer } from "~/components/ui/ContentDisclaimer";
import { useLocale } from "~/i18n";
import {
  ApiError,
  askPublicQuestion,
  cancelPublicRegistration,
  confirmPublicRegistration,
  fetchPublicEvent,
  fetchPublicRegistration,
  registerPublicly,
} from "~/lib/api";
import { addToCalendar } from "~/lib/calendar-export";
import { openExternal } from "~/lib/external-link";
import { LIVE_TOKEN_PARAM } from "~/lib/live-links";
import { queryKeys } from "~/lib/query-client";
import { turnstileBridgeUrl } from "~/lib/turnstile";
import { useAuthStore } from "~/store/auth-store";
import type { EventPublic, PublicRegistrationView } from "~/types/event";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The page's one column: the same gutters and width as a reading screen, no shell. */
function Page({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        minHeight: "100dvh",
        backgroundColor: "background.default",
        paddingInline: { xs: 2.5, md: 5 },
        paddingTop: "calc(24px + env(safe-area-inset-top))",
        paddingBottom: "calc(40px + env(safe-area-inset-bottom))",
      }}
    >
      <Box
        sx={{
          maxWidth: 760,
          marginInline: "auto",
          display: "flex",
          flexDirection: "column",
          gap: { xs: 3, md: 4 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Wordmark size={24} />
        </Box>
        {children}
      </Box>
    </Box>
  );
}

function EventHeader({ event }: { event: EventPublic }) {
  const { t } = useTranslation();
  return (
    <>
      {event.cover_image_url ? (
        <ArticleCover slug={event.slug} src={event.cover_image_url} alt="" />
      ) : null}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          maxWidth: READING_MEASURE,
        }}
      >
        <Typography variant="OV1" sx={{ color: "text.muted" }}>
          {t("live.eyebrow")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <EventStateBadge state={event.state} always />
          <EventTime event={event} withDate />
        </Box>
        <Typography
          variant="H1"
          sx={{ color: "text.primary" }}
          data-testid="live.title"
        >
          <WithHalfdot size={11}>{event.title}</WithHalfdot>
        </Typography>
        {event.summary ? (
          <Typography
            component="p"
            variant="H3"
            sx={{ color: "text.secondary" }}
          >
            {event.summary}
          </Typography>
        ) : null}
      </Box>
    </>
  );
}

/** The signed-out door: an address, consent, and the captcha when the API wants one. */
function RegistrationForm({ event }: { event: EventPublic }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const config = useAuthStore((state) => state.config);
  const captchaRequired = config?.captcha_required ?? false;

  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaNonce, setCaptchaNonce] = useState(0);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const handleCaptcha = useCallback(
    (token: string | null) => setCaptchaToken(token),
    [],
  );

  const register = useMutation({
    mutationFn: () =>
      registerPublicly(event.slug, {
        email: email.trim(),
        locale,
        captchaToken: captchaToken ?? undefined,
        marketingConsent: consent,
      }),
    onError: (error) => {
      if (captchaRequired) {
        // Turnstile tokens are single-use and the previous one is spent by the request.
        setCaptchaToken(null);
        setCaptchaNonce((nonce) => nonce + 1);
      }
      setErrorKey(
        error instanceof ApiError && error.status === 409
          ? "live.form.full"
          : "live.form.error",
      );
    },
  });

  const submit = (submitEvent: FormEvent) => {
    submitEvent.preventDefault();
    setErrorKey(null);
    if (!EMAIL_PATTERN.test(email.trim())) {
      setErrorKey("live.form.invalidEmail");
      return;
    }
    register.mutate();
  };

  if (register.isSuccess) {
    const accepted = register.data;
    return (
      <Paper
        variant="outlined"
        data-testid="live.sent"
        sx={{
          padding: { xs: 3, md: 4 },
          borderRadius: `${RADIUS.xl}px`,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <Typography variant="H2" sx={{ color: "text.primary" }}>
          {t("live.sent.title")}
        </Typography>
        <Typography variant="T2" sx={{ color: "text.secondary" }}>
          {t("live.sent.body", { email: email.trim() })}
        </Typography>
        {accepted.debug_confirm_url ? (
          // The same development affordance the sign-in code has: with OTP_ECHO_CODE
          // on, the API echoes the link so the flow can be walked without a mailbox.
          <Alert severity="info" data-testid="live.debug-link">
            {t("live.sent.debugLink")}:{" "}
            <a
              href={accepted.debug_confirm_url}
              data-testid="live.debug-link-anchor"
            >
              {accepted.debug_confirm_url}
            </a>
          </Alert>
        ) : null}
      </Paper>
    );
  }

  if (!event.registration_open) {
    return (
      <Alert severity="info" data-testid="live.closed">
        {t(event.spots_left === 0 ? "live.form.full" : "live.form.closed")}
      </Alert>
    );
  }

  return (
    <Paper
      component="form"
      variant="outlined"
      onSubmit={submit}
      // The form carries its own, translated message; the browser's bubble would not.
      noValidate
      data-testid="live.form"
      sx={{
        padding: { xs: 3, md: 4 },
        borderRadius: `${RADIUS.xl}px`,
        boxShadow: SHADOW.raised,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box>
        <Typography variant="H2" sx={{ color: "text.primary" }}>
          {t("live.form.title")}
        </Typography>
        <Typography variant="T2" sx={{ color: "text.secondary", marginTop: 1 }}>
          {t("live.form.body")}
        </Typography>
      </Box>
      <TextField
        type="email"
        label={t("live.form.email")}
        value={email}
        onChange={(changeEvent) => setEmail(changeEvent.target.value)}
        required
        autoComplete="email"
        fullWidth
        slotProps={{
          htmlInput: { "data-testid": "live.email", maxLength: 320 },
        }}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={consent}
            onChange={(changeEvent) => setConsent(changeEvent.target.checked)}
            data-testid="live.consent"
          />
        }
        label={t("live.form.consent")}
      />
      {captchaRequired ? (
        <Turnstile
          key={captchaNonce}
          onToken={handleCaptcha}
          bridgeUrl={turnstileBridgeUrl()}
        />
      ) : null}
      {errorKey ? (
        <Alert severity="error" data-testid="live.error">
          {t(errorKey)}
        </Alert>
      ) : null}
      <Button
        type="submit"
        variant="contained"
        size="large"
        disabled={register.isPending || (captchaRequired && !captchaToken)}
        data-testid="live.submit"
        sx={{ alignSelf: "flex-start" }}
      >
        {t("live.form.submit")}
      </Button>
      <Typography variant="T3" sx={{ color: "text.muted" }}>
        {t("live.form.privacy")}
      </Typography>
    </Paper>
  );
}

/** Behind the token: the place, the way in once live, the registrant's questions. */
function RegistrantPlace({
  token,
  place,
}: {
  token: string;
  place: PublicRegistrationView;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [cancelled, setCancelled] = useState(false);
  const [cancelErrorKey, setCancelErrorKey] = useState<string | null>(null);
  const key = queryKeys.publicRegistration(token);

  const ask = useMutation({
    mutationFn: (body: string) => askPublicQuestion(token, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  });
  const cancel = useMutation({
    mutationFn: () => cancelPublicRegistration(token),
    onSuccess: () => setCancelled(true),
    onError: () => setCancelErrorKey("live.place.cancelError"),
  });

  if (cancelled) {
    return (
      <Alert severity="success" data-testid="live.cancelled">
        {t("live.place.cancelled")}
      </Alert>
    );
  }

  const confirmed = place.status === "confirmed";
  const { event } = place;

  return (
    <>
      <Paper
        variant="outlined"
        data-testid={confirmed ? "live.confirmed" : "live.pending"}
        sx={{
          padding: { xs: 3, md: 4 },
          borderRadius: `${RADIUS.xl}px`,
          borderColor: confirmed ? "primary.main" : "divider",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {confirmed ? (
            <CheckCircleOutlineIcon sx={{ color: "primary.main" }} />
          ) : null}
          <Typography variant="H2" sx={{ color: "text.primary" }}>
            {t(
              confirmed
                ? "live.place.confirmedTitle"
                : "live.place.pendingTitle",
            )}
          </Typography>
        </Box>
        <Typography variant="T2" sx={{ color: "text.secondary" }}>
          {t(
            confirmed ? "live.place.confirmedBody" : "live.place.pendingBody",
            {
              email: place.email,
            },
          )}
        </Typography>
        {confirmed && place.stream_url && event.state === "live" ? (
          <Button
            variant="contained"
            size="large"
            endIcon={<OpenInNewIcon sx={{ fontSize: "1rem" }} />}
            onClick={() => void openExternal(place.stream_url!)}
            data-testid="live.enter-stream"
            sx={{ alignSelf: "flex-start" }}
          >
            {t("events.booking.enter")}
          </Button>
        ) : null}
        {confirmed && event.state === "live" ? (
          <Typography variant="T3" sx={{ color: "text.secondary" }}>
            {t("events.booking.liveHint")}
          </Typography>
        ) : null}
        {confirmed &&
        (event.state === "ended" || event.state === "recording") ? (
          <Typography variant="T3" sx={{ color: "text.secondary" }}>
            {t("events.booking.endedHint")}
          </Typography>
        ) : null}
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          {confirmed &&
          (event.state === "upcoming" || event.state === "soon") ? (
            <Button
              variant="outlined"
              startIcon={<EventAvailableOutlinedIcon fontSize="small" />}
              onClick={() =>
                void addToCalendar({ ...event, url: place.stream_url })
              }
              data-testid="live.add-to-calendar"
            >
              {t("events.addToCalendar")}
            </Button>
          ) : null}
          {event.state !== "ended" && event.state !== "recording" ? (
            <Button
              variant="text"
              color="inherit"
              disabled={cancel.isPending}
              onClick={() => cancel.mutate()}
              data-testid="live.cancel"
            >
              {t("live.place.cancel")}
            </Button>
          ) : null}
        </Box>
        {cancelErrorKey ? (
          <Alert severity="error">{t(cancelErrorKey)}</Alert>
        ) : null}
      </Paper>

      {confirmed ? (
        <Box sx={{ maxWidth: READING_MEASURE }}>
          <EventQuestions
            questions={place.questions}
            open={place.questions_open}
            onAsk={(body) => ask.mutateAsync(body)}
            testIdPrefix="live"
          />
        </Box>
      ) : null}
    </>
  );
}

/**
 * The public registration page — `/live/<slug>`, with or without a token.
 *
 * Without one it is the landing page marketing shares: the event, and a form that
 * takes an address. With one (`?t=`) it is the registrant's own page, reached from
 * every e-mail about the place: the confirmation link confirms it on arrival, and
 * afterwards the page is where the broadcast link appears, questions are asked and
 * the place can be given up. The same URL for all of it, so the registrant has one
 * link to keep.
 *
 * Outside every gate on purpose — an outsider has no session — and registered in the
 * signed-out route table too, or the sign-in screen would answer the link.
 */
export function LiveEventRoute() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { slug = "" } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get(LIVE_TOKEN_PARAM) ?? "";
  const status = useAuthStore((state) => state.status);
  const queryClient = useQueryClient();

  const event = useQuery({
    queryKey: queryKeys.publicEvent(locale, slug),
    queryFn: ({ signal }) => fetchPublicEvent(slug, locale, signal),
    enabled: slug.length > 0 && token.length === 0,
    refetchInterval: 60_000,
  });

  const place = useQuery({
    queryKey: queryKeys.publicRegistration(token),
    queryFn: ({ signal }) => fetchPublicRegistration(token, signal),
    enabled: token.length > 0,
    refetchInterval: 60_000,
  });

  // The confirmation is a POST the page makes once it knows the place is pending — a
  // mail client prefetching the link must not confirm a place nobody asked for, and a
  // GET with a side effect would do exactly that.
  const confirmedOnce = useRef<string | null>(null);
  const confirm = useMutation({
    mutationFn: () => confirmPublicRegistration(token),
    onSuccess: (view) =>
      queryClient.setQueryData(queryKeys.publicRegistration(token), view),
  });
  useEffect(() => {
    if (!place.data || place.data.status !== "pending") return;
    if (confirmedOnce.current === token) return;
    confirmedOnce.current = token;
    confirm.mutate();
    // `confirm` is a stable mutation object; listing it would re-fire on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [place.data, token]);

  const openInApp =
    status === "authenticated" ? (
      <Button
        component={RouterLink}
        to={`/events/${slug}`}
        variant="text"
        data-testid="live.open-app"
        sx={{ alignSelf: "flex-start" }}
      >
        {t("live.place.openApp")}
      </Button>
    ) : null;

  if (token) {
    if (
      place.isPending ||
      (confirm.isPending && place.data?.status === "pending")
    ) {
      return (
        <Page>
          <LoadingState
            label={t(
              confirm.isPending ? "live.place.confirming" : "live.loading",
            )}
          />
        </Page>
      );
    }
    const gone =
      place.error instanceof ApiError &&
      (place.error.status === 404 || place.error.status === 410);
    if (gone) {
      return (
        <Page>
          <Alert severity="warning" data-testid="live.gone">
            <Typography variant="H3" component="p" sx={{ marginBottom: 0.5 }}>
              {t("live.place.goneTitle")}
            </Typography>
            {t("live.place.goneBody")}
          </Alert>
          {openInApp}
        </Page>
      );
    }
    if (place.isError || !place.data) {
      return (
        <Page>
          <ErrorState
            message={t("live.errorBody")}
            onRetry={() => void place.refetch()}
          />
        </Page>
      );
    }
    const full =
      confirm.error instanceof ApiError && confirm.error.status === 409;
    const confirmGone =
      confirm.error instanceof ApiError &&
      (confirm.error.status === 404 || confirm.error.status === 410);
    if (confirmGone) {
      return (
        <Page>
          <Alert severity="warning" data-testid="live.gone">
            <Typography variant="H3" component="p" sx={{ marginBottom: 0.5 }}>
              {t("live.place.goneTitle")}
            </Typography>
            {t("live.place.goneBody")}
          </Alert>
          {openInApp}
        </Page>
      );
    }
    // Anything else that went wrong — the network, a 5xx — is recoverable: say so and
    // offer the attempt again, rather than a pending message telling the visitor to
    // open the link they are already looking at.
    const confirmFailed = confirm.isError && !full;
    return (
      <Page>
        <EventHeader event={place.data.event} />
        {full ? (
          <Alert severity="warning" data-testid="live.full">
            <Typography variant="H3" component="p" sx={{ marginBottom: 0.5 }}>
              {t("live.place.fullTitle")}
            </Typography>
            {t("live.place.fullBody")}
          </Alert>
        ) : confirmFailed ? (
          <Alert
            severity="error"
            data-testid="live.confirm-failed"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => confirm.mutate()}
                data-testid="live.confirm-retry"
              >
                {t("common.retry")}
              </Button>
            }
          >
            {t("live.place.confirmError")}
          </Alert>
        ) : (
          <RegistrantPlace token={token} place={place.data} />
        )}
        {openInApp}
        <Box sx={{ maxWidth: READING_MEASURE }}>
          <Markdown>{place.data.event.body}</Markdown>
        </Box>
        <ContentDisclaimer />
      </Page>
    );
  }

  if (event.isPending) {
    return (
      <Page>
        <LoadingState label={t("live.loading")} />
      </Page>
    );
  }
  if (
    event.isError &&
    event.error instanceof ApiError &&
    event.error.status === 404
  ) {
    return (
      <Page>
        <EmptyState message={t("live.notFoundBody")} />
      </Page>
    );
  }
  if (event.isError || !event.data) {
    return (
      <Page>
        <ErrorState
          message={t("live.errorBody")}
          onRetry={() => void event.refetch()}
        />
      </Page>
    );
  }

  return (
    <Page>
      <EventHeader event={event.data} />
      <RegistrationForm event={event.data} />
      {openInApp}
      <Box sx={{ maxWidth: READING_MEASURE }}>
        <Markdown>{event.data.body}</Markdown>
      </Box>
      <ContentDisclaimer />
    </Page>
  );
}
